/**
 * useBrainstormWebSocket Hook
 *
 * React hook for managing Brainstorm Mode WebSocket connections.
 * Provides real-time multi-window synchronization for Brainstorm (Design) mode.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  brainstormWebsocketManager,
  BrainstormWebSocketMessage,
} from '../services/brainstormWebsocketService';

export interface UseBrainstormWebSocketOptions {
  onMessage?: (message: BrainstormWebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  onReconnect?: () => void;
}

export type BrainstormSocketState = 'CONNECTING' | 'OPEN' | 'CLOSING' | 'CLOSED' | 'UNKNOWN';

export function useBrainstormWebSocket() {
  const [socketState, setSocketState] = useState<BrainstormSocketState>('CLOSED');
  const [lastSocketEvent, setLastSocketEvent] = useState<string>('');
  const [isReconnecting, setIsReconnecting] = useState<boolean>(false);
  const [reconnectAttempt, setReconnectAttempt] = useState<number>(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const wsThreadRef = useRef<string | null>(null);
  const wsConnectPromiseRef = useRef<Promise<void> | null>(null);
  const wsConnectingThreadRef = useRef<string | null>(null);
  const socketStateRef = useRef<BrainstormSocketState>('CLOSED');
  socketStateRef.current = socketState;

  const connectToThread = useCallback(
    async (threadId: string, options: UseBrainstormWebSocketOptions = {}): Promise<void> => {
      if (!threadId) return;

      console.log(`🔌 [Brainstorm] Connecting to WebSocket for thread: ${threadId}`);

      // If already connected to this thread, do nothing
      if (socketStateRef.current === 'OPEN' && wsThreadRef.current === threadId) {
        console.log(`✅ [Brainstorm] Already connected to thread ${threadId}`);
        return;
      }

      // If a connect is already in-flight for this thread, reuse it
      if (wsConnectPromiseRef.current && wsConnectingThreadRef.current === threadId) {
        console.log(`🔄 [Brainstorm] Connection already in-flight for thread ${threadId}`);
        return wsConnectPromiseRef.current;
      }

      const token = localStorage.getItem('token') || '';

      if (!token) {
        console.warn(`[Brainstorm] No auth token available, skipping WebSocket connection for thread: ${threadId}`);
        return;
      }

      // Disconnect from previous thread if needed
      if (wsThreadRef.current && wsThreadRef.current !== threadId) {
        try {
          brainstormWebsocketManager.disconnect(wsThreadRef.current);
        } catch {
          // Ignore disconnect errors
        }
      }

      wsThreadRef.current = threadId;
      wsConnectingThreadRef.current = threadId;

      wsConnectPromiseRef.current = new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          wsConnectPromiseRef.current = null;
          wsConnectingThreadRef.current = null;
          reject(new Error('Brainstorm WebSocket connection timeout'));
        }, 5000);

        brainstormWebsocketManager.connect(threadId, token, {
          onMessage: (message) => {
            console.log('📨 [Brainstorm] WebSocket message received:', message.type);
            setLastSocketEvent(message.type);
            options.onMessage?.(message);
          },
          onConnect: () => {
            clearTimeout(timeout);
            setSocketState('OPEN');
            setIsReconnecting(false);
            setReconnectAttempt(0);
            console.log('✅ [Brainstorm] WebSocket connected to thread:', threadId);
            wsConnectPromiseRef.current = null;
            wsConnectingThreadRef.current = null;

            if (reconnectTimeoutRef.current) {
              window.clearTimeout(reconnectTimeoutRef.current);
              reconnectTimeoutRef.current = null;
            }

            options.onConnect?.();
            resolve();
          },
          onReconnect: () => {
            clearTimeout(timeout);
            setSocketState('OPEN');
            setIsReconnecting(false);
            setReconnectAttempt(0);
            console.log('🔄 [Brainstorm] WebSocket reconnected:', threadId);
            wsConnectPromiseRef.current = null;
            wsConnectingThreadRef.current = null;

            if (reconnectTimeoutRef.current) {
              window.clearTimeout(reconnectTimeoutRef.current);
              reconnectTimeoutRef.current = null;
            }

            options.onReconnect?.();
            resolve();
          },
          onDisconnect: () => {
            clearTimeout(timeout);
            setSocketState('CLOSED');
            setIsReconnecting(true);
            console.log('🔌 [Brainstorm] WebSocket disconnected:', threadId);
            wsConnectPromiseRef.current = null;
            wsConnectingThreadRef.current = null;

            if (reconnectTimeoutRef.current) {
              window.clearTimeout(reconnectTimeoutRef.current);
            }
            reconnectTimeoutRef.current = window.setTimeout(() => {
              console.log('⏰ [Brainstorm] Reconnection timeout reached');
              setIsReconnecting(false);
              setReconnectAttempt(0);
            }, 15000);

            options.onDisconnect?.();
          },
          onError: () => {
            clearTimeout(timeout);
            setSocketState('UNKNOWN');
            setIsReconnecting(true);
            console.warn('❌ [Brainstorm] WebSocket error:', threadId);
            wsConnectPromiseRef.current = null;
            wsConnectingThreadRef.current = null;

            if (reconnectTimeoutRef.current) {
              window.clearTimeout(reconnectTimeoutRef.current);
            }
            reconnectTimeoutRef.current = window.setTimeout(() => {
              console.log('⏰ [Brainstorm] Reconnection timeout after error');
              setIsReconnecting(false);
              setReconnectAttempt(0);
            }, 120000);

            options.onError;
            reject(new Error('Brainstorm WebSocket connection failed'));
          },
          onReconnectAttempt: (attempt) => {
            setReconnectAttempt(attempt);
            setIsReconnecting(true);
            console.log(`🔄 [Brainstorm] Reconnection attempt ${attempt} for thread: ${threadId}`);
          },
        });
      });

      return wsConnectPromiseRef.current;
    },
    [] // socketState read via ref to avoid re-creating this callback on every state change
  );

  const disconnect = useCallback(() => {
    if (wsThreadRef.current) {
      brainstormWebsocketManager.disconnect(wsThreadRef.current);
      wsThreadRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    setSocketState('CLOSED');
    setIsReconnecting(false);
    setReconnectAttempt(0);
  }, []);

  const disconnectAll = useCallback(() => {
    brainstormWebsocketManager.disconnectAll();
    wsThreadRef.current = null;

    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    setSocketState('CLOSED');
    setIsReconnecting(false);
    setReconnectAttempt(0);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current);
      }
      disconnectAll();
    };
  }, [disconnectAll]);

  return {
    socketState,
    lastSocketEvent,
    isReconnecting,
    reconnectAttempt,
    connectToThread,
    disconnect,
    disconnectAll,
    connectedThreadId: wsThreadRef.current,
  };
}
